/* Copyright (c) 2017 Backpack Health LLC */
/* global IS_ANDROID_BUILD */

import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import classnames from 'classnames';

import './ShareCardModal.scss';

import ShareCardEdit from './shareCardEdit/ShareCardEdit.controller';
import RegisterNewJewelryController from './shareCardJewelry/RegisterNewJewelry.controller';
import { Grid } from '../../components/Grid';
import RouteNames from '../../appAuthorized/RouteNames';
import { useUIState } from '../../hooks/useUIState';
import { useNavigate } from '../../hooks/useNavigate';
import { useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import ShareCardSummary from './shareCardSummary/ShareCardSummary';

const VERTICAL_MARGIN = IS_ANDROID_BUILD ? 4 : 32;

interface Props {
  className?: string;
  style?: any;
}

const HighLevelModal: React.FC<Props> = (props) => {
  return (
    <div className={classnames('cmp-sharecardmodal', props.className)} style={props.style}>
      <div className="_content">
        {props.children}
      </div>
    </div>
  );
};

const NoShareCardModal = (props) => {
  const { hideShareCardModal } = props;

  React.useEffect(() => {
    hideShareCardModal();
  }, []);

  return null;
};

const ShareCardEditModal = (props) => {
  const { shareCardId, showShareCardModal, closeShareCard, isCreate, definitionId, newColorId } = props;

  const [state, setState] = React.useState(0);
  const [scState, scActions] = useShareCardListForActiveProfile();
  const shareCard = isCreate ? scActions.instantiateNewShareCard(definitionId, newColorId) : scActions.instantiate(shareCardId);

  const renderEditModal = () => {
    showShareCardModal(
      <HighLevelModal style={{ paddingTop: VERTICAL_MARGIN }}>
        <Grid justifyContent="center" alignItems="center" height="100%">
          <ShareCardEdit
            {...props}
            isCreate={isCreate}
            definitionId={definitionId}
            newColorId={newColorId}
            shareCard={shareCard}
            goBackToEdit={() => {
              setState(state + 1);
            }}
          />
        </Grid>
      </HighLevelModal>,
      closeShareCard,
    );
  };

  React.useEffect(() => {
    renderEditModal();
  }, [state]);

  if (isCreate || shareCard || !scState.isLoaded) {
    return null;
  } else {
    return <Redirect to={RouteNames.share()} />;
  }
};

const ShareCardRegisterNewJewelryModal = (props) => {
  const { showShareCardModal, shareCardId, closeShareCard } = props;

  React.useEffect(() => {
    showShareCardModal(
      <HighLevelModal style={{ paddingTop: VERTICAL_MARGIN }}>
        <Grid justifyContent="center" alignItems="center" height="100%">
          <RegisterNewJewelryController
            shareCardId={shareCardId}
          />
        </Grid>
      </HighLevelModal>,
      closeShareCard,
    );
  }, []);

  return null;
};

interface ShareCardViewModalProps {
  shareCardId: number;
  isDataLoaded: boolean;
  showShareCardModal(c, o): void;
  closeShareCard(): void;
}
const ShareCardViewModal: React.FC<ShareCardViewModalProps> = (props) => {
  const { shareCardId, isDataLoaded, showShareCardModal, closeShareCard } = props;

  const [, navActions] = useNavigate();

  React.useEffect(() => {
    if (!isDataLoaded) { return; }

    showShareCardModal(
      <HighLevelModal>
        <ShareCardSummary
          shareCardId={shareCardId}
          editShareCard={() => {
            navActions.goTo(RouteNames.shareCard(shareCardId));
          }}
        />
      </HighLevelModal>,
      closeShareCard,
    );
  }, [isDataLoaded]);

  return null;
};

interface ShareCardModalProps {
  shareCardId: number;
  isCreate: boolean;
  isDataLoaded: boolean;
  definitionId: number;
  newColorId: number;
  onSubmit(d, o?): void;
}

const ShareCardModal: React.FC<ShareCardModalProps> = (props) => {
  const { onSubmit, isCreate, shareCardId, isDataLoaded, definitionId, newColorId } = props;
  const [, uiActions] = useUIState();
  const [, navActions] = useNavigate();
  const [, scActions] = useShareCardListForActiveProfile();

  const showShareCardModal = (el, onDismiss) => {
    uiActions.showDarkBigBlur(el, {
      onDismiss,
      isBackgroundClickDisabled: true,
    });
  };

  const closeShareCard = () => {
    navActions.goBack(RouteNames.share());
  };

  return (
    <Switch>
      <Route path={RouteNames.shareCardView(':shareCardId')}>
        <ShareCardViewModal
          isDataLoaded={isDataLoaded}
          shareCardId={shareCardId}
          closeShareCard={() => {
            navActions.goBackTo(RouteNames.share());
          }}
          showShareCardModal={showShareCardModal}
        />
      </Route>

      <Route path={RouteNames.shareCardRegisterNewJewelry(':shareCardId')}>
        <ShareCardRegisterNewJewelryModal
          shareCardId={shareCardId}
          closeShareCard={closeShareCard}
          showShareCardModal={showShareCardModal}
        />
      </Route>

      <Route path={RouteNames.shareCard(':shareCardId')}>
        <ShareCardEditModal
          shareCardId={shareCardId}
          isCreate={isCreate}
          definitionId={definitionId}
          newColorId={newColorId}
          onSubmit={onSubmit}
          showShareCardModal={showShareCardModal}
          closeShareCard={closeShareCard}
          deleteShareCard={() => {
            if (isCreate) {
              uiActions.dismissAllModals();
              navActions.goBackTo(RouteNames.share());
            } else {
              scActions.apiDelete(shareCardId).then(() => {
                uiActions.dismissAllModals();
                navActions.goTo(RouteNames.share());
              });
            }
          }}
        />
      </Route>

      <Route path="/">
        <NoShareCardModal
          hideShareCardModal={() => {
            uiActions.dismissModal();
          }}
        />
      </Route>
    </Switch>
  );
};

export default ShareCardModal;
